import { useSeo } from "@/hooks/useSeo";
import { PageHero } from "@/components/common/PageHero";
import { Reveal } from "@/components/common/Reveal";
import { PillLink } from "@/components/common/PillLink";

const LETTERS = [
  { l: "M", w: "Minds", color: "bg-lavender" },
  { l: "U", w: "United", color: "bg-mint" },
  { l: "C", w: "Curiosity", color: "bg-lemon" },
  { l: "C", w: "Creativity", color: "bg-blush" },
  { l: "I", w: "Innovation", color: "bg-lavender" },
];

const AUDIENCE = [
  { title: "Students", text: "Years 5–10 learners who love asking why, building things and figuring out how the world works.", color: "bg-mint" },
  { title: "Parents & caregivers", text: "Families looking for learning that builds thinking skills and confidence, not just exam marks.", color: "bg-lemon" },
  { title: "Homeschooling families", text: "Structured, inquiry-based courses that fit alongside learning at home.", color: "bg-blush" },
];

export default function About() {
  useSeo("About", "About MUCCI Academy — Minds United for Curiosity, Creativity & Innovation. Inquiry-based learning for Years 5–10 students across New Zealand.");
  return (
    <div data-testid="about-page">
      <PageHero testid="about-hero" eyebrow="About MUCCI" color="bg-lemon" title="Minds United for Curiosity, Creativity & Innovation"
        intro="MUCCI Academy is an inquiry-based education brand for students in Years 5–10 in New Zealand." />
      <section className="section-y" aria-labelledby="name-title">
        <div className="container-x">
          <Reveal><h2 id="name-title" className="text-4xl sm:text-5xl font-semibold tracking-tight">What's in a name?</h2></Reveal>
          <ul className="mt-10 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
            {LETTERS.map((x, i) => (
              <Reveal as="li" key={x.w} delay={i * 0.07}>
                <div className={`rounded-card border border-ink/10 ${x.color} p-6 transition-transform duration-300 hover:-translate-y-1`}>
                  <span className="font-heading text-6xl font-semibold leading-none" aria-hidden="true">{x.l}</span>
                  <p className="mt-3 font-heading text-xl">{x.w}</p>
                </div>
              </Reveal>
            ))}
          </ul>
        </div>
      </section>
      <section className="section-y" aria-labelledby="mission-title">
        <div className="container-x grid gap-12 lg:grid-cols-2">
          <Reveal className="relative">
            <h2 id="mission-title" className="text-4xl sm:text-5xl font-semibold tracking-tight">Why we exist</h2>
            <p className="mt-6 text-lg leading-relaxed text-ink-soft" data-testid="why-exist-p1">
              We believe children learn best when they are curious, ask questions, and discover answers for themselves. Through hands-on
              projects and meaningful inquiry, we help learners develop critical thinking, creativity, and problem-solving skills that go
              beyond exam results.
            </p>
            <p className="mt-4 text-lg leading-relaxed text-ink-soft" data-testid="why-exist-p2">
              Our goal is to nurture self-directed, curious learners who take ownership of their learning, develop their own approaches to
              investigation and research, and find genuine joy in exploring new ideas.
            </p>
            <p className="mt-4 text-lg leading-relaxed text-ink-soft" data-testid="why-exist-p3">
              Whether learning online, at partner schools, at our locations, or through a blend of in-person and online experiences, we aim
              to make meaningful learning accessible to children across New Zealand.
            </p>
          </Reveal>
          <div className="grid gap-5">
            {AUDIENCE.map((a, i) => (
              <Reveal key={a.title} delay={i * 0.08}>
                <div className={`rounded-card ${a.color} p-7`} data-testid={`audience-${i}`}>
                  <h3 className="text-2xl font-semibold">{a.title}</h3>
                  <p className="mt-2 leading-relaxed">{a.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
      <div className="container-x mt-8 flex flex-wrap gap-4">
        <PillLink to="/courses" variant="primary" arrow data-testid="about-courses-link">Explore courses</PillLink>
        <PillLink to="/contact#enquire" variant="outline" data-testid="about-contact-link">Get in touch</PillLink>
      </div>
    </div>
  );
}
